import { toast } from "react-toastify";
import { Box, ThemeOptions, ThemeProvider, createTheme } from "@mui/material";
import { Col, Form, Input, Modal, Row, Switch } from "antd";
import { FormInstance, Spin } from "antd/lib";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../hooks/api";
import { useCountries } from "../../hooks/useCountries";
import { useLanguages } from "../../hooks/useLanguages";
import { pathAPI } from "../../utils/pathApi";
import { fileToBlob } from "../../utils/utils";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import CommonMultiSelect, { SelectOption } from "../common/CommonMultiSelect";
import UploadImage from "../common/UploadImage";

/**
 * Creates a custom theme for CommonMultiSelect to match Ant Design form fields
 * @param customHeight - Optional custom height (e.g., "40px", "48px")
 */
const createCustomSelectTheme = (customHeight?: string): ThemeOptions => {
  // Calculate internal height (total height - borders)
  // Ant Design Input: 32px total height
  const totalHeight = customHeight || "32px";
  const numericHeight = parseInt(totalHeight);
  const internalHeight = `${numericHeight - 2}px`; // Subtract 2px for borders (1px top + 1px bottom)

  return {
    components: {
      // Remove margin from FormControl to align with Ant Design
      MuiFormControl: {
        styleOverrides: {
          root: {
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
      // Force Select to use outlined variant (removes underline)
      MuiSelect: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          select: {
            // Configurable height - use exact height for consistency
            height: internalHeight,
            minHeight: internalHeight,
            display: "flex",
            alignItems: "center",
            padding: "4px 11px", // Match Ant Design's horizontal padding
            lineHeight: internalHeight, // Ensure vertical centering
            // Remove any default padding that might affect alignment
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            // Remove all default margins
            margin: 0,
            // Remove underline artifacts (::before, ::after)
            "&::before": {
              display: "none !important",
            },
            "&::after": {
              display: "none !important",
            },
            // Default border color
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--app-border) !important", // Ant Design's default border
              borderRadius: "6px", // Match Ant Design's border radius
              // Ensure continuous border (no label notch)
              "& legend": {
                display: "none", // Hide legend to prevent border notch
              },
            },
            // Hover state
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--app-border) !important",
            },
            // Focused state - change to #ba2156 to match Ant Design focus
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#ba2156 !important",
              borderWidth: "1px !important", // Keep 1px like Ant Design (MUI defaults to 2px)
              boxShadow: "none !important", // Remove MUI's default box-shadow on focus
            },
          },
          input: {
            // Ensure proper vertical centering with reduced padding
            padding: "4px 11px !important", // Match Ant Design's padding
            height: internalHeight,
            boxSizing: "border-box",
            // Remove any borders/underlines from input itself
            border: "none !important",
            borderBottom: "none !important",
            "&::before": {
              display: "none !important",
              borderBottom: "none !important",
            },
            "&::after": {
              display: "none !important",
              borderBottom: "none !important",
            },
          },
        },
      },
      // Remove underline from Input/InputBase (legacy safety)
      MuiInput: {
        styleOverrides: {
          root: {
            "&::before": {
              display: "none !important",
              borderBottom: "none !important",
            },
            "&::after": {
              display: "none !important",
              borderBottom: "none !important",
            },
          },
        },
      },
      // Remove underline from InputBase (affects Select)
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&::before": {
              display: "none !important",
              borderBottom: "none !important",
            },
            "&::after": {
              display: "none !important",
              borderBottom: "none !important",
            },
            // Remove any underline styling
            borderBottom: "none !important",
          },
          input: {
            "&::before": {
              display: "none !important",
              borderBottom: "none !important",
            },
            "&::after": {
              display: "none !important",
              borderBottom: "none !important",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // Don't change label color on focus to match Ant Design
            "&.Mui-focused": {
              color: "var(--app-text)", // Match Ant Design's default label color
            },
          },
        },
      },
    },
  };
};

// Default theme with 32px height to match Ant Design Input
const customSelectTheme = createCustomSelectTheme("40px");

interface AddModalProps {
  open: boolean;
  onCancel: () => void;
  mutate?: () => void;
}

const getCategoryLabel = (item: any): string => {
  if (item.i18n_names?.en !== undefined) {
    return `${item.i18n_names.en || ""} ${
      item?.country_short_code ? ` (${item?.country_short_code}) ` : ""
    }`;
  }
  return `${item.name || ""} ${
    item?.code ? ` (${item?.code}) ` : ""
  }`;
};

const AddModal: React.FC<AddModalProps> = ({ mutate, onCancel, ...props }) => {
  const { t } = useTranslation();
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };
  const [{ data: languages, isLoading: loadingLanguages }] = useLanguages();
  const [{ data: countries }] = useCountries();
  const formRef = useRef<FormInstance>(null);
  const [serviceImage, setServiceImage] = useState<File | null>(null);
  const [background, setBackground] = useState<File | null>(null);
  const [homeImage, setHomeImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedNationals, setSelectedNationals] = useState<Array<string | number>>(["all"]);
  const [nationalError, setNationalError] = useState(false);

  // Transform countries to SelectOption format for CommonMultiSelect
  const countryOptions: SelectOption[] = useMemo(() => {
    if (!countries) return [];
    return [
      { label: t("category.add.all"), value: "all" },
      ...countries.map((country) => ({
        label: getCategoryLabel(country),
        value: country.id,
      })),
    ];
  }, [countries, t]);

  // Sync selectedNationals with form field
  useEffect(() => {
    formRef.current?.setFieldValue("national", selectedNationals);
  }, [selectedNationals]);

  const allCountryIds = useMemo(
    () => (countries || []).map((country) => country.id),
    [countries]
  );

  // Once countries are loaded, expand the default "all" into every item
  useEffect(() => {
    if (
      allCountryIds.length > 0 &&
      selectedNationals.length === 1 &&
      selectedNationals[0] === "all"
    ) {
      setSelectedNationals(["all", ...allCountryIds]);
    }
  }, [allCountryIds, selectedNationals]);

  // Keep "All" and the individual items in sync
  const handleNationalChange = (values: Array<string | number>) => {
    const hadAll = selectedNationals.includes("all");
    const hasAll = values.includes("all");
    const items = values.filter((v) => v !== "all");

    let next: Array<string | number>;
    if (hasAll && !hadAll) {
      // "All" just ticked -> tick everything
      next = ["all", ...allCountryIds];
    } else if (!hasAll && hadAll && items.length === allCountryIds.length) {
      // "All" just unticked -> untick everything
      next = [];
    } else if (allCountryIds.length > 0 && items.length === allCountryIds.length) {
      // every item ticked manually -> tick "All"
      next = ["all", ...items];
    } else {
      // some item unticked -> untick "All"
      next = items;
    }

    if (next.length > 0) {
      setNationalError(false);
    }
    setSelectedNationals(next);
  };

  const addSuccess = () => {
    toast.success(t("category.messages.add_success"));
  };

  const deleteError = () => {
    toast.error(t("category.messages.error"));
  };

  const postCategory = async (data: any) => {
    try {
      setLoading(true);
      await api.POST(pathAPI.categoryDetail, data, {}, true);
      addSuccess();
      if (mutate) mutate();
      // Delay closing modal to show success message
      setTimeout(() => {
        onCancel();
        setLoading(false);
      }, 1000);
    } catch (error) {
      deleteError();
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    // Validate required fields
    const name = formRef.current?.getFieldValue(["name"]);
    if (!name || name.trim() === "") {
      toast.error(t("category.validation.name_required"));
      return;
    }

    const national = formRef.current?.getFieldValue(["national"]);
    if (!national || national.length === 0) {
      setNationalError(true);
      toast.error(t("category.validation.national_required"));
      return;
    }

    // Clear error if validation passes
    setNationalError(false);

    if (!background) {
      toast.error(t("category.add.background_required"));
      return;
    }

    if (!serviceImage) {
      toast.error(t("category.add.service_image_required"));
      return;
    }

    if (!homeImage) {
      toast.error(t("category.add.home_image_required"));
      return;
    }

    const formData = new FormData();
    if (serviceImage) {
      const blob = await fileToBlob(serviceImage);
      formData.append("serviceImage", blob as Blob, serviceImage.name || "default-filename.png");
    }
    if (background) {
      const blob = await fileToBlob(background);
      formData.append("backgroundImage", blob as Blob, background.name || "default-filename.png");
    }
    if (homeImage) {
      const blob = await fileToBlob(homeImage);
      formData.append("homeImage", blob as Blob, homeImage.name || "default-filename.png");
    }
    formData.append(
      "name", formRef.current?.getFieldValue(["name"]) || "");
    formData.append(
      "active", `${formRef.current?.getFieldValue(["active"]) ? 1 : 0}`
    );
    formData.append(
      "showHomeImage", `${formRef.current?.getFieldValue(["showHomeImage"]) ? 1 : 0}`
    );
    formData.append(
      "color", formRef.current?.getFieldValue(["color"]) || "#000"
    );

    // Handle countryIds: if "all" is selected, send [0], otherwise send the selected country IDs
    const nationalValues = formRef.current?.getFieldValue(["national"]) || [];
    if (nationalValues.includes("all")) {
      formData.append("countryIds", "0");
    } else {
      nationalValues.forEach((e: any) => {
        formData.append("countryIds", e || "");
      });
    }

    postCategory(formData);
  };

  const sectionStyle = {
    border: "1px solid var(--app-border-light)",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
  };
  const sectionTitle = (text: string) => (
    <Box fontSize={"14px"} fontWeight={600} color={"var(--app-text)"} mb={2}>
      {text}
    </Box>
  );

  return (
    <Modal
      onCancel={onCancel}
      {...props}
      destroyOnClose={true}
      width={880}
      style={{ top: 40 }}
      styles={{
        content: { borderRadius: "12px", overflow: "hidden", padding: 0 },
      }}
      title={null}
      closable={false}
      footer={
        <Box display={"flex"} justifyContent={"flex-end"} gap={2} px={3} py={2} borderTop={"1px solid var(--app-border-light)"}>
          <CancelButton onClick={onCancel} disabled={loading}>
            {t("btn.cancel")}
          </CancelButton>
          <ActionButton
            type="submit"
            onClick={onFinish}
            loading={loading}
            disabled={loading || nationalError}
          >
            {t("btn.ok")}
          </ActionButton>
        </Box>
      }
    >
      <Box bgcolor={"#ba2156"} color={"#fff"} px={3} py={2}>
        <Box fontSize={"18px"} fontWeight={700} lineHeight={1.3}>
          {t("category.add.title")}
        </Box>
        <Box fontSize={"12px"} sx={{ opacity: 0.85 }}>
          {t("category.add.subtitle")}
        </Box>
      </Box>
      <Box sx={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
      {loadingLanguages && (
        <Box textAlign={"center"} py={4}>
          <Spin tip={t("category.add.loading")} />
        </Box>
      )}
      {!loadingLanguages && (
        <Box px={3} py={3}>
          <Form ref={formRef} onFinish={onFinish} {...layout}>
            <div style={sectionStyle}>
              {sectionTitle(t("category.sections.basic_information"))}
              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name={"name"}
                    label={<span>{t("category.fields.name")}<span className="text-red-500"> *</span></span>}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <Input allowClear style={{ height: "40px" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<span>{t("category.fields.national")}<span className="text-red-500"> *</span></span>}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    validateStatus={nationalError ? "error" : ""}
                    help={nationalError ? t("category.validation.national_required") : ""}
                  >
                    <ThemeProvider theme={(outerTheme) => createTheme(outerTheme, customSelectTheme)}>
                      <CommonMultiSelect
                        options={countryOptions}
                        value={selectedNationals}
                        onChange={handleNationalChange}
                        onClose={() => {
                          // Validate when dropdown closes
                          if (selectedNationals.length === 0) {
                            setNationalError(true);
                          }
                        }}
                        placeholder={t("category.fields.national")}
                        size="medium"
                      />
                    </ThemeProvider>
                  </Form.Item>
                  <Form.Item name={"national"} hidden initialValue={["all"]} />
                </Col>
              </Row>
            </div>

            <div style={sectionStyle}>
              {sectionTitle(t("category.sections.display_settings"))}
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={8}>
                  <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={2} px={2} py={1.5} border={"1px solid var(--app-border-light)"} borderRadius={"8px"}>
                    <Box fontSize={"14px"} fontWeight={500}>
                      {t("category.fields.color")}<span className="text-red-500"> *</span>
                    </Box>
                    <Form.Item name={"color"} initialValue="#7b4747" style={{ margin: 0 }}>
                      <input
                        type="color"
                        defaultValue="#7b4747"
                        style={{
                          width: "64px",
                          height: "32px",
                          border: "1px solid var(--app-border)",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                        onChange={(e) => {
                          formRef.current?.setFieldValue("color", e.target.value);
                        }}
                      />
                    </Form.Item>
                  </Box>
                </Col>
                <Col xs={24} md={8}>
                  <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={2} px={2} py={1.5} border={"1px solid var(--app-border-light)"} borderRadius={"8px"}>
                    <Box fontSize={"14px"} fontWeight={500}>{t("category.fields.active")}</Box>
                    <Form.Item name={"active"} valuePropName="checked" initialValue={true} style={{ margin: 0 }}>
                      <Switch checkedChildren={t("category.switch.on")} unCheckedChildren={t("category.switch.off")} />
                    </Form.Item>
                  </Box>
                </Col>
                <Col xs={24} md={8}>
                  <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={2} px={2} py={1.5} border={"1px solid var(--app-border-light)"} borderRadius={"8px"}>
                    <Box fontSize={"14px"} fontWeight={500}>{t("category.fields.show_home_image")}</Box>
                    <Form.Item name={"showHomeImage"} valuePropName="checked" initialValue={true} style={{ margin: 0 }}>
                      <Switch checkedChildren={t("category.switch.on")} unCheckedChildren={t("category.switch.off")} />
                    </Form.Item>
                  </Box>
                </Col>
              </Row>
            </div>

            <div style={{ ...sectionStyle, marginBottom: 0 }}>
              {sectionTitle(t("category.sections.images"))}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={"serviceImage"}
                    label={<span>{t("category.fields.service_image")}<span className="text-red-500"> *</span></span>}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    style={{ marginBottom: 0 }}
                  >
                    <UploadImage
                      width={"100%"}
                      height={"200px"}
                      onChangeFile={setServiceImage}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={"background"}
                    label={<span>{t("category.fields.background")}<span className="text-red-500"> *</span></span>}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    style={{ marginBottom: 0 }}
                  >
                    <UploadImage
                      width={"100%"}
                      height={"200px"}
                      onChangeFile={setBackground}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name={"homeImage"}
                    label={<span>{t("category.fields.home_image")}<span className="text-red-500"> *</span></span>}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    style={{ marginBottom: 0 }}
                  >
                    <UploadImage
                      width={"100%"}
                      height={"200px"}
                      onChangeFile={setHomeImage}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </Form>
        </Box>
      )}
      </Box>
    </Modal>
  );
};

export default AddModal;
